// Libraries
import { flexRender } from '@tanstack/react-table';
import { Th } from '@chakra-ui/react';
// Hooks
import { useBorderRight } from '@hooks/index';
// Types
import { TableHeaderProps } from './TableHeader.types';

/**
 * TableHeader component represents a header cell in a table.
 *
 * @component
 * @param {TableHeaderProps} props - The component props.
 * @param {Header<EeoiScoresBody, unknown>} props.header - The header data for the cell.
 * @param {BorderRightBreakpoint<CellsName>} props.cellsWithBorderRightMap - Responsive map of all cell columns with border right
 * @returns {ReactElement} TableHeader component.
 */
export const TableHeader = <TableBody, CellsName>(props: TableHeaderProps<TableBody, CellsName>) => {
  const { header, cellsWithBorderRightMap, ...rest } = props;
  const { id, colSpan, column, isPlaceholder, getSize, getContext } = header;

  /**
   * Determines the border right style for the cell based on the header and width mapping.
   */
  const borderRight = useBorderRight<CellsName>({
    cellId: id,
    cellsWithBorderRightMap,
  });

  const size = `${getSize()}px`;

  return (
    <Th
      key={id}
      colSpan={colSpan}
      w={size}
      maxW={size}
      textTransform='none'
      p={0}
      onClick={column.getToggleSortingHandler()}
      border='none'
      borderRight={borderRight}
      {...rest}
    >
      {isPlaceholder ? null : flexRender(column.columnDef.header, getContext())}
    </Th>
  );
};
